import { buildPageableParams, Page, PageableArgs } from '../common';
import { queryDownloadFile } from '../common/pageable/query-download-file';
import { queryFile } from '../common/pageable/query-file';
import { OrganismeFichierDto, OrganismeFichierType } from '../organisme-fichier';
import { servicesApi } from '../services';
import { OrganismeFichierProjection } from './types';

const ORGANISME_FICHIER_URL_PREFIX = (id: number) => `organismes/${id}/fichiers`;

const organismeFichierApi = servicesApi.injectEndpoints!({
  endpoints: (build) => ({

    getOrganismeFichiers: build.query<Page<OrganismeFichierDto>, GetOrganismeFichiers, any>({
      query: ({ id, ...args }) => ({
        url: ORGANISME_FICHIER_URL_PREFIX(id),
        params: buildPageableParams(args),
      }),
      providesTags: ['OrganismeFichiers'],
    }),

    updateOrganismeFichier: build.mutation<void, UpdateOrganismeFichierArgs, any>({
      query: ({ id, fichierType, fichierSubType }) => ({
        url: `${ORGANISME_FICHIER_URL_PREFIX(id)}/${fichierType}`,
        method: 'PATCH',
        body: { subType: fichierSubType },
      }),
      invalidatesTags: ['OrganismeFichiers'],
    }),

    replaceOrganismeFichier: build.mutation<void, UpsertOrganismeFichierArgs, any>({
      query: ({ id, fichierType, formData }) => ({
        url: `${ORGANISME_FICHIER_URL_PREFIX(id)}/${fichierType}`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: ['OrganismeFichiers'],
    }),

    downloadOrganismeFichier: build.mutation<void, DownloadOrganismeFichierMutationArgs, any>({
      queryFn: ({ id, fichierType }, _api, _extraOptions, baseQuery) => queryDownloadFile({
        baseQuery,
        url: `${ORGANISME_FICHIER_URL_PREFIX(id)}/${fichierType}/resource`,
      }),
    }),

    downloadOrganismeFichierFile: build.mutation<File, DownloadOrganismeFichierMutationArgs, any>({
      queryFn: ({ id, fichierType }, _api, _extraOptions, baseQuery) => queryFile({
        baseQuery,
        url: `${ORGANISME_FICHIER_URL_PREFIX(id)}/${fichierType}/resource`,
      }),
    }),
  }),
});

export interface GetOrganismeFichiers extends PageableArgs<OrganismeFichierProjection> {
  id: number;
}

export interface UpdateOrganismeFichierArgs {
  id: number;
  fichierType: OrganismeFichierType;
  fichierSubType: string;
}

export interface UpsertOrganismeFichierArgs {
  id: number;
  fichierType: OrganismeFichierType;
  formData: FormData; // file: File & fichierSubType: string
}

export interface DownloadOrganismeFichierMutationArgs {
  id: number;
  fichierType: OrganismeFichierType;
}

export const {
  useUpdateOrganismeFichierMutation,
  useReplaceOrganismeFichierMutation,
  useGetOrganismeFichiersQuery,
  useLazyGetOrganismeFichiersQuery,
  useDownloadOrganismeFichierMutation,
  useDownloadOrganismeFichierFileMutation,
} = organismeFichierApi;